import React, { useState } from 'react';
import { Button, Space, Tag, Tooltip, Typography } from 'antd';
import { DownOutlined, LinkOutlined, UpOutlined } from '@ant-design/icons';
import { LeadOpsMatch } from '../../../api/leadOps';
import { InventoryRow } from './InventoryRow';
import { AdFormatTriedDetail, buildAdFormatGaps, isArchivedInventory, isSellableInventory } from '../utils/adFormats';

const { Text } = Typography;

type PublisherSummaryProps = {
  primaryLeadMatch: LeadOpsMatch | null;
  primaryIsExact: boolean;
  otherLeadsCount: number;
  websiteMatches: LeadOpsMatch[];
  inventoryMatches: LeadOpsMatch[];
  isExactDomainMatch: (match: LeadOpsMatch) => boolean;
};

const triedTooltip = (detail: AdFormatTriedDetail | undefined): string => {
  if (!detail) return 'Tried previously';
  const lines: string[] = [];
  if (detail.finishedCount > 0) lines.push(`Finished · ${detail.finishedCount}`);
  if (detail.failedCount > 0) lines.push(`Failed · ${detail.failedCount}`);
  return lines.length > 0 ? lines.join('\n') : 'Tried previously';
};

export const PublisherSummary: React.FC<PublisherSummaryProps> = ({
  primaryLeadMatch,
  primaryIsExact,
  otherLeadsCount,
  websiteMatches,
  inventoryMatches,
  isExactDomainMatch,
}) => {
  const [spotsExpanded, setSpotsExpanded] = useState(false);
  const liveInventory = inventoryMatches.filter((match) => !isArchivedInventory(match));
  const activeSpots = liveInventory.length;
  const sellableSpots = liveInventory.filter(isSellableInventory).length;
  const gaps = buildAdFormatGaps(inventoryMatches);
  const triedByLabel = new Map(gaps.triedDetails.map((detail) => [detail.label, detail]));
  const extraWebsites = Math.max(0, websiteMatches.length - 3);
  const leadStage = primaryLeadMatch?.lead_status || primaryLeadMatch?.group_name;
  const gapColumns = [
    gaps.active.length > 0 && {
      key: 'active',
      label: 'Active now',
      formats: gaps.active,
      color: 'green' as const,
      never: false,
    },
    gaps.tried.length > 0 && {
      key: 'tried',
      label: 'Tried, did not stick',
      formats: gaps.tried,
      color: undefined,
      never: false,
    },
    gaps.never.length > 0 && {
      key: 'never',
      label: 'Never tried',
      formats: gaps.never,
      color: undefined,
      never: true,
    },
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    formats: string[];
    color?: 'green';
    never: boolean;
  }>;

  return (
    <Space direction="vertical" size={8} className="leadops-publisher-summary" style={{ width: '100%' }}>
      <div className="leadops-publisher-identity-band">
        <div className="leadops-publisher-identity-item">
          <Text type="secondary" className="leadops-gap-label">Lead</Text>
          {primaryLeadMatch ? (
            <>
              <div className="leadops-publisher-identity-line">
                <Text strong>{primaryLeadMatch.domain || primaryLeadMatch.name}</Text>
                <Tag color={primaryIsExact ? 'green' : 'default'} style={{ margin: 0 }}>
                  {primaryIsExact ? 'Exact' : 'Related'}
                </Tag>
              </div>
              {(primaryLeadMatch.owner_name || leadStage) && (
                <Text type="secondary" className="leadops-publisher-identity-meta">
                  {[primaryLeadMatch.owner_name && `Manager: ${primaryLeadMatch.owner_name}`, leadStage]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              )}
              {otherLeadsCount > 0 && (
                <Text type="secondary" className="leadops-publisher-identity-meta">
                  +{otherLeadsCount} more lead{otherLeadsCount > 1 ? 's' : ''}
                </Text>
              )}
            </>
          ) : (
            <Text type="secondary">No lead in CRM yet</Text>
          )}
        </div>

        <div className="leadops-publisher-identity-divider" />

        <div className="leadops-publisher-identity-item">
          <Text type="secondary" className="leadops-gap-label">Website record</Text>
          {websiteMatches.length > 0 ? (
            <Space direction="vertical" size={2}>
              {websiteMatches.slice(0, 3).map((match) => (
                <div key={`${match.board_id}-${match.item_id}`} className="leadops-publisher-identity-line">
                  <Text>{match.domain || match.name}</Text>
                  {isExactDomainMatch(match) && <Tag color="green" style={{ margin: 0 }}>Exact</Tag>}
                  {match.url && (
                    <Button
                      size="small"
                      type="text"
                      icon={<LinkOutlined />}
                      onClick={() => window.open(match.url, '_blank', 'noopener,noreferrer')}
                    />
                  )}
                </div>
              ))}
              {extraWebsites > 0 && (
                <Text type="secondary">+{extraWebsites} more website{extraWebsites > 1 ? 's' : ''}</Text>
              )}
            </Space>
          ) : (
            <Text type="secondary">No separate website record</Text>
          )}
        </div>

        <div className="leadops-publisher-identity-divider" />

        <div className="leadops-publisher-identity-item">
          <Text type="secondary" className="leadops-gap-label">Inventory spots</Text>
          {activeSpots > 0 ? (
            <Space size={6} wrap>
              <Tag color="purple" style={{ margin: 0 }}>{activeSpots} active</Tag>
              {sellableSpots > 0 && <Tag color="green" style={{ margin: 0 }}>{sellableSpots} sellable</Tag>}
            </Space>
          ) : (
            <Text type="secondary">No active spots</Text>
          )}
        </div>

        {primaryLeadMatch?.url && (
          <>
            <div className="leadops-publisher-identity-divider" />
            <div className="leadops-publisher-identity-action">
              <Button
                size="small"
                icon={<LinkOutlined />}
                onClick={() => window.open(primaryLeadMatch.url, '_blank', 'noopener,noreferrer')}
              >
                Open
              </Button>
            </div>
          </>
        )}
      </div>

      {gapColumns.length > 0 && (
        <section className="leadops-gap-section">
          <Text strong className="leadops-gap-title">Ad format coverage</Text>
          <div
            className="leadops-gap-grid"
            style={{ gridTemplateColumns: `repeat(${gapColumns.length}, minmax(0, 1fr))` }}
          >
            {gapColumns.map((col) => (
              <div key={col.key} className="leadops-gap-col">
                <Text type="secondary" className="leadops-gap-label">{col.label}</Text>
                <div className="leadops-gap-tags">
                  {col.formats.map((format) => {
                    const tag = (
                      <Tag
                        color={col.color}
                        className={col.never ? 'leadops-gap-never-tag' : undefined}
                        style={{ margin: 0 }}
                      >
                        {format}
                      </Tag>
                    );
                    if (col.key === 'active') {
                      return <React.Fragment key={`${col.key}-${format}`}>{tag}</React.Fragment>;
                    }
                    if (col.key === 'never') {
                      return (
                        <Tooltip key={`${col.key}-${format}`} title="No inventory cards with this format">
                          {tag}
                        </Tooltip>
                      );
                    }
                    return (
                      <Tooltip
                        key={`${col.key}-${format}`}
                        title={<span style={{ whiteSpace: 'pre-line' }}>{triedTooltip(triedByLabel.get(format))}</span>}
                      >
                        {tag}
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {liveInventory.length > 0 && (
        <div className="leadops-publisher-spots-toggle-row">
          <Button
            block
            onClick={() => setSpotsExpanded((v) => !v)}
            icon={spotsExpanded ? <UpOutlined /> : <DownOutlined />}
          >
            {spotsExpanded ? 'Hide Inventory' : `Show Inventory (${liveInventory.length})`}
          </Button>
          {spotsExpanded && (
            <div className="leadops-publisher-spot-list">
              {liveInventory.map((match) => (
                <InventoryRow
                  key={`${match.board_id}-${match.item_id}`}
                  match={match}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </Space>
  );
};
